import React from 'react';
import { useIntl } from 'react-intl';
import { BarSegmentChartSC } from './bar-segment-chart.styled';
import BarChartIndicator from './bar-segment-chart-indicator/bar-segment-chart-indicator';

export interface IBarSegmentChartRange {
  /**
   * position on bar
   */
  pos: number;
  /**
   * name of segment
   */
  name: string;
  /**
   * min value of segment
   */
  from: number;
  /**
   * Max value of segment
   */
  to: number;
  /**
   * Color of segment
   */
  color: string;
}

interface IChartLabel {
  valueKey: string;
  displayedValue: string;
  descriptionKey: string;
  unit: string;
  position: number;
  customCss?: string;
  color: string;
  updateColorFromRange: boolean;
  addUnitToDisplayedValue: boolean;
  addRangeToDisplayedValue: boolean;
  hidden: boolean;
  replaceDescriptionWithRange: boolean;
}

export interface IBarSegmentConfig {
  labels: IChartLabel[];
  ranges: IBarSegmentChartRange[];
  indicatorKey: string;
}

/**
 * Get color for range by name
 */
export function getColorByValue(ranges: IBarSegmentChartRange[], value: number) {
  const currentRange = ranges.find((range) => value > range.from && value <= range.to);
  return currentRange?.color || 'rgba(41, 49, 87, 0.6)';
}

export interface BarSegmentChartProps {
  indicatorConfig: { left: string; color: string };
  lineWidth: string;
  labels: IBarSegmentConfig['labels'];
  ranges: IBarSegmentConfig['ranges'];
  isEmpty: boolean;
}

/**
 * Get range for value and given ranges
 */
export function getCurrentRange(value: number = 0, ranges: IBarSegmentChartRange[]) {
  return (
    ranges.find((range) => range.from <= value && value <= range.to) || ranges[ranges.length - 1]
  );
}

const BarSegmentChart: React.FC<BarSegmentChartProps> = ({
  indicatorConfig,
  lineWidth,
  labels,
  ranges,
  isEmpty,
}) => {
  const intl = useIntl();

  return (
    <BarSegmentChartSC>
      <div className="bmi-widget-graph">
        <div className="bmi-widget-graph__values">
          {ranges.slice(1, ranges.length).map((range, i) => {
            if (i === ranges.length - 1) {
              return null;
            }
            return (
              <div style={{ width: lineWidth }} key={i}>
                <span>{range.from}</span>
              </div>
            );
          })}
        </div>
        <div className={'indicator-container'}>
          <BarChartIndicator indicatorConfig={indicatorConfig} />
        </div>
        <div className="bmi-widget-graph-line">
          {ranges.map((range, i) => {
            return <div key={i} style={{ backgroundColor: range.color, width: lineWidth }} />;
          })}
        </div>
        <div className="bmi-widget-graph-labels">
          {ranges.map((range, i) => {
            return (
              <span key={i} style={{}}>
                {intl.formatMessage({ id: `life_id:status.${range.name}` })}
              </span>
            );
          })}
        </div>
      </div>

      <div className="bmi-widget-data">
        {labels.map((label, i) => {
          if (label.hidden) {
            return <div key={i} style={{ color: label.color }} />;
          }
          const className = label.customCss
            ? `bmi-widget-data__item ${label.customCss}`
            : 'bmi-widget-data__item';
          return (
            <div className={className} key={i} style={{ color: label.color }}>
              <span>{isEmpty ? '-' : label.displayedValue}</span>
              <span>{intl.formatMessage({ id: label.descriptionKey })}</span>
            </div>
          );
        })}
      </div>
    </BarSegmentChartSC>
  );
};

export default BarSegmentChart;
